// Visual catalog — single source of truth lives in the backend at
// `backend/app/data/DashboardVisuals.json`, served by
// `GET /api/designers/visuals-catalog`. The visual designer fetches it on
// mount so any edit to that file is picked up automatically without
// touching the frontend.

import { fetchVisualsCatalog } from '@/api/designers'

export interface ColumnGroupSpec {
  GroupName: string
  GroupDescription: string
  GroupIcon: string
  AllowedColumnTypes: 'ANY' | 'NUMBER' | 'DATE' | 'STRING'
  MinColumns: number
  MaxColumns: number | string
  StandardName: string
}

export interface VisualOptionSpec {
  optionId: string
  optionName: string
  optionHeading: string
  dataType: 'boolean' | 'string' | 'number'
  defaultValue: string
  listOfValues: string
  allowChange: string
  optionType?: string
  showWhen?: string
}

export interface VisualCatalogEntry {
  ID: string
  VisualName: string
  VisualType: string
  DisplayOrder: string
  VisualIcon: string
  className: string
  OutputType: string
  options: VisualOptionSpec[]
  ColumnGroups: ColumnGroupSpec[]
}

// Fallback minimal entry — used only when the backend catalog can't be
// loaded so the editor still mounts with a usable Tabular default.
const FALLBACK_TABULAR: VisualCatalogEntry = {
  ID: 'insight_tabular',
  VisualName: 'Tabular',
  VisualType: 'Standard/UserDefined',
  VisualIcon: 'Tabular.svg',
  DisplayOrder: '19',
  className: 'InsightTabular',
  OutputType: 'JSON',
  options: [],
  ColumnGroups: [
    {
      GroupName: 'Columns',
      GroupDescription: '',
      GroupIcon: 'Column.svg',
      AllowedColumnTypes: 'ANY',
      MinColumns: 0,
      MaxColumns: 0,
      StandardName: 'columns',
    },
  ],
}

let _catalog: VisualCatalogEntry[] | null = null
let _inflight: Promise<VisualCatalogEntry[]> | null = null

/** Promise-returning loader — call from `useEffect` in the designer page. */
export async function loadVisualCatalog(): Promise<VisualCatalogEntry[]> {
  if (_catalog) return _catalog
  if (_inflight) return _inflight

  _inflight = (async () => {
    try {
      const res = await fetchVisualsCatalog()
      const visuals = (res?.Visuals as VisualCatalogEntry[]) || []
      _catalog = visuals.length > 0 ? visuals : [FALLBACK_TABULAR]
      return _catalog
    } catch (err) {
      console.error('Failed to load visual catalog from backend:', err)
      _catalog = [FALLBACK_TABULAR]
      return _catalog
    } finally {
      _inflight = null
    }
  })()
  return _inflight
}

/** Synchronous accessor — returns whatever was last loaded. Call this from
 *  helpers that run on already-loaded data (sanitizers, lookups). If nothing
 *  has been loaded yet returns a single-entry Tabular fallback so callers
 *  don't crash. */
export function getVisualCatalog(): VisualCatalogEntry[] {
  return _catalog || [FALLBACK_TABULAR]
}

/** Compatibility shim — older code paths import VISUAL_CATALOG as a constant.
 *  We expose it as a property getter so it always returns the latest loaded
 *  array. (Plain `export const` would freeze at module-load time before the
 *  fetch completes.) */
export const VISUAL_CATALOG = new Proxy([] as VisualCatalogEntry[], {
  get(_t, prop) {
    const cat = getVisualCatalog()
    if (prop === 'length') return cat.length
    if (typeof prop === 'string' && /^\d+$/.test(prop)) return cat[Number(prop)]
    if (prop === Symbol.iterator) return cat[Symbol.iterator].bind(cat)
    // Array methods (find, map, filter, ...): forward to the live array.
    const value = (cat as unknown as Record<PropertyKey, unknown>)[prop]
    if (typeof value === 'function') return (value as (...args: unknown[]) => unknown).bind(cat)
    return value
  },
})

/** Look up a catalog entry tolerantly: by VisualName first (case-insensitive,
 *  whitespace-tolerant), then by ID, then fallback to Tabular. */
export function findCatalogEntry(
  visualName?: string,
  visualId?: string
): VisualCatalogEntry {
  const norm = (s: string | undefined) =>
    (s || '').toLowerCase().replace(/[\s_-]+/g, '')

  const cat = getVisualCatalog()
  const targetName = norm(visualName)
  if (targetName) {
    const byName = cat.find((c) => norm(c.VisualName) === targetName)
    if (byName) return byName
  }
  const targetId = norm(visualId)
  if (targetId) {
    const byId = cat.find((c) => norm(c.ID) === targetId)
    if (byId) return byId
  }
  return cat[0] || FALLBACK_TABULAR
}
